import { createSelector } from 'reselect';

export const selectStagingBuilding = createSelector(
  [(state) => state.entities.staging, (state, buildingId) => buildingId],
  (
    {
      pointsByEquipId,
      equipmentByBuilding,
      filters,
      selectedPoints,
      selectedEquipment,
      validation: { points, equipment },
      loaded,
    },
    buildingId
  ) => ({
    loaded: loaded[buildingId] ?? false,
    equipment: equipmentByBuilding[buildingId] ?? [],
    pointsByEquipId: pointsByEquipId[buildingId] ?? {},
    pointValidation: points[buildingId] ?? {},
    equipValidation: equipment[buildingId] ?? {},
    filters,
    selectedPoints,
    selectedEquipment,
  })
);

export const selectStagingWithValidation = createSelector(selectStagingBuilding, (staging) => {
  const { equipment, pointsByEquipId, pointValidation, equipValidation } = staging;
  // map { equip_id: bool } of whether we've validated at all
  const equipValidated = equipment.reduce((o, e) => {
    // eslint-disable-next-line no-param-reassign
    o[e.equip_id] = equipValidation[e.equip_id] != null;
    return o;
  }, {});
  // combine equip validation and point validation to see if there are
  // any errors that may not be visible based on UI state
  const equipMessages = equipment.reduce((o, e) => {
    if (equipValidation[e.equip_id] != null) {
      const pointMessages = (pointsByEquipId[e.equip_id] ?? [])
        .map((point) => (typeof point === 'string' ? point : point.topic))
        .map((topic) => (pointValidation[topic]?.length ? `Error in ${topic}` : null))
        .filter((t) => t != null);

      if (pointMessages.length > 0) {
        // eslint-disable-next-line no-param-reassign
        o[e.equip_id] = equipValidation[e.equip_id].concat(pointMessages);
      } else {
        // eslint-disable-next-line no-param-reassign
        o[e.equip_id] = equipValidation[e.equip_id];
      }
    }
    return o;
  }, {});
  return { ...staging, equipValidated, equipMessages };
});

export const selectStagingEquipment = createSelector(selectStagingWithValidation, (staging) => {
  const { equipment, filters } = staging;
  const filteredEquipment = equipment.filter((equip) => filters.every((f) => f.filter(equip)));

  return { ...staging, filteredEquipment };
});

export const selectStagingPoints = createSelector(selectStagingWithValidation, (staging) => {
  const { pointsByEquipId, filters } = staging;
  const points = Object.keys(pointsByEquipId)
    .reduce((arr, eid) => arr.concat(pointsByEquipId[eid].filter((point) => point)), [])
    .filter((point) => filters.every((f) => f.filter(point)));

  return { ...staging, points };
});
